import { logInfo } from 'setup/logger/logInfo';
import { wait } from 'shared/utils/wait';
import { repairServiceWorker } from '../repairServiceWorker';
import { alignServiceWorkerWithPage } from './alignServiceWorkerWithPage';
import { getFailedStages, isServiceWorkerAlignmentFailed, isTakeOverStuck } from './model';
import { type AlignmentAttempt, ServiceWorkerAlignment } from './types';
import { reportServiceWorkerAlignmentFailure } from './reportServiceWorkerAlignmentFailure';

/**
 * How long to wait before each new attempt to align. A deploy moves hosts one
 * after another, so the worker the page is served may be the old one for a
 * moment: the later attempts find the new one without the user's help.
 */
export const ALIGNMENT_RETRY_DELAYS_MS = [5_000, 15_000] as const;

/**
 * The end of every recovery that did not align the worker: reports it once,
 * with every attempt, then repairs the worker (which reloads the page; once
 * per session). Resolves whether the repair ran.
 */
async function reportAndRepair(attempts: AlignmentAttempt[], trigger: string): Promise<boolean> {
  await reportServiceWorkerAlignmentFailure(attempts);
  return repairServiceWorker(trigger);
}

/**
 * After the first attempt to align failed: aligns again at the given delays
 * and, when all fail, reports and repairs the worker. A worker that was told
 * to take over and did not is stuck, not late: it is repaired at once,
 * without the pauses that only keep the user on the blocking screen.
 * Resolves the alignment reached; `failed` when none was.
 */
export async function recoverServiceWorkerAlignment(firstAttempt: AlignmentAttempt): Promise<ServiceWorkerAlignment> {
  const attempts = [firstAttempt];
  let latestAttempt = firstAttempt;
  for (const delayMs of ALIGNMENT_RETRY_DELAYS_MS) {
    if (isTakeOverStuck(latestAttempt)) {
      break;
    }
    await wait(delayMs);
    latestAttempt = await alignServiceWorkerWithPage();
    attempts.push(latestAttempt);
    if (!isServiceWorkerAlignmentFailed(latestAttempt.alignment)) {
      logInfo('service-worker-alignment-recovered', {
        alignment: latestAttempt.alignment,
        delayMs,
        stages: getFailedStages(attempts),
      });
      return latestAttempt.alignment;
    }
  }
  await reportAndRepair(attempts, isTakeOverStuck(latestAttempt) ? 'take-over-stuck' : 'alignment-failed');
  return ServiceWorkerAlignment.failed;
}

/**
 * The refresh of the blocking screen: aligns again and, when that still
 * fails, reports it and repairs the worker; otherwise reloads, so the page
 * starts on the aligned worker.
 */
export async function alignServiceWorkerOrRepair(trigger: string): Promise<void> {
  const attempt = await alignServiceWorkerWithPage();
  if (isServiceWorkerAlignmentFailed(attempt.alignment) && (await reportAndRepair([attempt], trigger))) {
    // The repair reloads the page.
    return;
  }
  window.location.reload();
}
